import { resolveName } from "../utils/messageUtils";
/**
 * Select the appropriate logging endpoint based on environment
 * All environments use Render-hosted logging services
 */
function getBaseLoggingUrl() {
  const host = window.location.hostname;
  if (host.includes("dev.endpointhealth.ca")) return "https://audit-logger-dev.onrender.com";
  if (host.includes("uat.endpointhealth.ca")) return "https://audit-logger-uat.onrender.com";
  if (host.includes("launch.endpointhealth.ca")) return "https://audit-logger-prd.onrender.com";
  return "https://audit-logger-dev.onrender.com";
}

const BASE_URL = getBaseLoggingUrl();
const LOG_URL = `${BASE_URL}/log`;
const PRO_URL = `${BASE_URL}/pro`;

/**
 * Log a FHIR AuditEvent by POSTing to the logging server
 * @param {object} auditEvent - a FHIR AuditEvent resource
 * @param {object} store - Redux store instance
 */
export async function logEvent(auditEvent, store, accessToken) {

  if (!accessToken)
    accessToken = store.getState().config.authToken; //LEFT OFF HERE. This is a race condition in redux. Have to ensure a value before proceeding
  
  console.log("Logging AuditEvent:", auditEvent);
  console.log("Logging to URL:", LOG_URL);
  console.log("Using access token:", accessToken);

  if (!auditEvent?.resourceType || auditEvent.resourceType !== "AuditEvent") {
    console.warn("⚠️ Invalid resource passed to logEvent");
    return;
  }

  if (!accessToken) {
    console.warn("⚠️ Missing access token for audit logging");
    return;
  }

  try {
    const response = await fetch(LOG_URL, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${accessToken}`
      },
      body: JSON.stringify(auditEvent)
    });

    if (!response.ok) {
      console.warn("❌ Audit log failed:", response.status);
    } else {
      console.log("✅ AuditEvent logged successfully");
    }
  } catch (e) {
    console.warn("⚠️ Failed to log AuditEvent:", e);
  }
}

/**
 * Log provider metadata by POSTing to the /pro endpoint
 * @param {object|object[]} providerData
 * @param {object} store - Redux store instance
 */
export async function logProvider(providerData, store) {
  const accessToken = store.getState().config.authToken;
  console.log("Logging provider data:", providerData);
  console.log("Logging to URL:", PRO_URL);
  //console.log("Using access token:", accessToken);

  if (!providerData || (Array.isArray(providerData) && providerData.length === 0)) {
    console.warn("⚠️ No provider data to log");
    return;
  }

  if (!accessToken) {
    console.warn("⚠️ Missing access token for provider logging");
    return;
  }

  try {
    const response = await fetch(PRO_URL, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${accessToken}`
      },
      body: JSON.stringify(providerData)
    });

    if (!response.ok) {
      console.warn("❌ Provider log failed:", response.status);
    } else {
      console.log("✅ Provider data logged successfully");
    }
  } catch (e) {
    console.warn("⚠️ Failed to log provider data:", e);
  }
}


export function buildAuditEvent({ action, subtype, agent, entity, description, outcome = "0", store }) {
  const orgConfig = store?.getState().config?.orgConfig || {};
  const fhirServer = orgConfig?.iss || "unknown-server";
  
  return {
    resourceType: "AuditEvent",
    id: crypto.randomUUID(),
    type: {
      system: "http://terminology.hl7.org/CodeSystem/audit-event-type",
      code: "rest",
      display: "RESTful Operation"
    },
    subtype: [subtype],
    action,
    recorded: new Date().toISOString(),
    outcome,
    description,
    source: {
      site: orgConfig?.clientId || "unknown-org",
      observer: { reference: fhirServer }
    },
    agent: [agent],
    entity: entity ? [entity] : []
  };
}

/**
 * Build a FHIR AuditEvent for SMART login / data access
 * @param {object} params - includes practitioner, patient, outcome, store
 * @returns {object} FHIR AuditEvent
 */
export function logAccessEvent({ practitioner, patient, outcome = "0", store, accessToken }) {
  const event = buildAuditEvent({
    action: "L",
    subtype: {
      system: "http://hl7.org/fhir/restful-interaction",
      code: "login",
      display: "Login"
    },
    agent: {
      who: {
        reference: `Practitioner/${practitioner?.id || "unknown"}`,
        display: `${practitioner?.name?.[0]?.given?.join(" ") || ""} ${practitioner?.name?.[0]?.family || ""}`.trim() || "Unknown"
      },
      requestor: true
    },
    entity: {
      what: {
        reference: `Patient/${patient?.id || "unknown"}`,
        display: patient?.name?.[0]?.text || "Unknown"
      },
      role: { code: "1", display: "Patient" }
    },
    description: "SMART-on-FHIR login",
    outcome,
    store
  });

  logEvent(event, store, accessToken);

}

/**
 * Build a FHIR AuditEvent for sending a Communication
 * @param {object} params - includes communication, outcome, store
 * @returns {object} FHIR AuditEvent
 */
export function logCommEvent({ communication, outcome = "0", store }) {
  const senderRef = communication?.sender?.reference || "";
  const senderName = resolveName(senderRef, store);

  // Map recipients into AuditEvent.entity entries
  const recipientEntities = (communication?.recipient || []).map(r => {
    const ref = r?.reference || "unknown";
    console.log(r);
    console.log(ref);

    const display = resolveName(ref, store) || "Unknown Recipient";
    return {
      what: {
        reference: ref,
        display
      },
      role: { code: "recipient", display: "Message Recipient" },
      description: "Communication recipient"
    };
  });

  // Base Communication entity
  const communicationEntity = {
    what: {
      reference: `Communication/${communication?.id || "unknown"}`,
      display: communication?.topic?.text || "Message"
    },
    role: { code: "4", display: "Communication" }
  };

  const event = buildAuditEvent({
    action: "C",
    subtype: {
      system: "http://hl7.org/fhir/restful-interaction",
      code: "create",
      display: "Create"
    },
    agent: {
      who: {
        reference: senderRef,
        display: senderName
      },
      requestor: true
    },
    // Merge Communication entity + recipient entities
    entity: [communicationEntity, ...recipientEntities],
    description: "User sent a communication message",
    outcome,
    store
  });

  logEvent(event, store);
}

/**
 * Build a FHIR AuditEvent for an error or failure event
 * @param {object} params - includes errorType, error, agent, entity, description, outcome, store
 * @returns {object} FHIR AuditEvent
 */
export function logErrorEvent({ errorType, error, agent, entity, description, outcome, store }) {
  console.log("Logging error event", error);
  let computedOutcome = "8";
  const status = error?.status || error?.response?.status;

  if (status) {
    if (status >= 400 && status < 500) computedOutcome = "4";
    else if (status >= 500) computedOutcome = "8";
  } else if (error?.severity === "minor") computedOutcome = "4";
  else if (error?.severity === "fatal") computedOutcome = "12";

  if (outcome) computedOutcome = outcome;

  const message =
    description ||
    error?.message ||
    "An unexpected error occurred during FHIR interaction.";

  const subtype = {
    system: "http://hl7.org/fhir/restful-interaction",
    code: errorType || "exception",
    display: "Error Event"
  };

  const resolvedAgent = agent || {
    who: { display: "System" },
    requestor: false
  };

  const resolvedEntity = entity
    ? entity
    : error?.resourceId
      ? {
        what: {
          reference: error.resourceId,
          display: "Related resource"
        }
      }
      : undefined;

  const event = buildAuditEvent({
    action: "E",
    subtype,
    agent: resolvedAgent,
    entity: resolvedEntity,
    description: message,
    outcome: computedOutcome,
    store
  });

  event.outcomeDesc = message;
  logEvent(event, store);

  //return auditEvent;
}